import type {
  LifeRecord,
  EarthRow,
  EarthDivergence,
  LifeCareerStage,
  LifeFuturePath,
  LifeTimelineEntry,
  LifeBeat,
} from "../../types";
import type { NarrativeContext, NarrativeResult } from "../../narrative";
import { ENGINE_VERSION } from "../../lifeChapters";
import {
  lifeStream,
  ageFor,
  clampLevel,
  pick,
  pickDistinct,
  listJoin,
  cap,
  lower,
} from "./lifeHelpers";
import { UNEXPECTED_LADDERS } from "./unexpected.life.banks";

/**
 * Builds a valid, fully populated, rich LifeRecord for The Unexpected Self
 * in the Multiverse Life Dossier (free).
 *
 * Inverts Earth assumptions about identity, success, work, social status,
 * and belonging through an "inverted ladder" selected deterministically
 * via ctx.stream("life.inversion").
 */
export function buildUnexpectedLife(
  ctx: NarrativeContext,
  base: NarrativeResult
): LifeRecord {
  // Independent seeded streams under "life.*"
  const ageRng = lifeStream(ctx, "unexpected.age");
  const invRng = lifeStream(ctx, "unexpected.inversion");
  const bioRng = lifeStream(ctx, "unexpected.bio");
  const workRng = lifeStream(ctx, "unexpected.work");
  const homeRng = lifeStream(ctx, "unexpected.home");
  const formRng = lifeStream(ctx, "unexpected.formative");
  const dayRng = lifeStream(ctx, "unexpected.day");
  const futRng = lifeStream(ctx, "unexpected.futures");
  const compRng = lifeStream(ctx, "unexpected.comparison");

  // 1. Inverted Ladder selection (1 of 12 distinct paradigms)
  const ladder = pick(UNEXPECTED_LADDERS, invRng);

  // 2. Age & Influence
  // Integer 19..84
  const age = ageFor(ageRng, 32, 58);
  const influenceScore = clampLevel(ctx.axes.influence, 28, 92);

  // Universe / Speculative non-biological check
  const isHostile = ctx.condition === "hostile";
  const isMarginal = ctx.condition === "marginal";

  // Relationships from base.dossier.relationships
  const rel0 = base.dossier.relationships[0]?.who || "Caelen";
  const rel0Role = base.dossier.relationships[0]?.role || "Senior Mentor";
  const rel1 = base.dossier.relationships[1]?.who || "Vespera";
  const rel1Role = base.dossier.relationships[1]?.role || "Civic Registrar";
  const rel2 = base.dossier.relationships[2]?.who || "Orlo";
  const rel2Role = base.dossier.relationships[2]?.role || "Companion Artisan";

  // Age note
  const ageNote = isHostile
    ? `Calculated as ${age} standard biological equivalent cycles within this high-energy radiation environment.`
    : `Standard solar planetary years, measured from the founding of the district's first unhurried hearth.`;

  // 3. Identity & Biography (4-5 paragraphs, each 2-4 sentences)
  const bioP1 = isHostile
    ? `${base.name} emerged into existence within the extreme, radiant currents of ${ctx.universe.name}. Here, conventional biology is replaced by resonant energetic coherence, yet consciousness retains an unmistakable human warmth. Rather than struggling against the brutal ambient thermodynamics, ${base.name} learned to navigate reality through the principles of ${ladder.name.toLowerCase()}, finding balance where Earth observers would expect only annihilating void.`
    : `${base.name} came of age in the quiet, terracotta-and-stone districts of ${ctx.universe.name}, where daily life is ordered by ${ladder.name.toLowerCase()}. Unlike Earth societies that demand frantic noise and perpetual personal expansion, this world taught ${base.name} from childhood that ${lower(ladder.invertedRule)}. Surrounded by neighbors who measured character by patient integrity rather than commercial conquest, ${base.name} discovered a natural affinity for deliberate, thoughtful work.`;

  const bioP2 = `${base.story.origin} This early foundation grounded ${base.name} in the nuances of ${base.occupation.toLowerCase()}. While peers in other realms might have pursued abstract corporate dominance, ${base.name} chose to invest heart and hands directly into the fabric of the immediate neighborhood, mastering methods that honor local trust over distant laurels.`;

  const bioP3 = `${base.story.turningPoint} That decisive moment redefined ${base.name}'s relationship to authority and civic standing. By choosing ${base.choices[0]?.choice ? lower(base.choices[0].choice) : "an unconventional path of authentic devotion"}, ${base.name} proved that real influence is won not through coercion or self-promotion, but by remaining steadfast when institutional pressures urge compromise.`;

  const bioP4 = `${base.story.today} Today, carrying the respected title of ${base.title}, ${base.name} operates with a quiet, unshakeable confidence that inspires younger companions. Friends like ${rel2} and counselors like ${rel0} frequently visit the workshop, seeking advice on how to navigate complex civic tensions with grace, humor, and principled calm.`;

  const bioP5 = `Looking across the arc of this life, what Earth might misread as an eccentric outsider existence is recognized here as an exemplary success. ${base.name} has built a legacy that will endure not in dry bureaucratic archives, but in the enduring gratitude of a community made gentler and more resilient by their presence.`;

  const biography = [bioP1, bioP2, bioP3, bioP4, bioP5];

  // 4. Work & Career (4 stages, 5-6 responsibilities, 4-6 expertise)
  const careerHistory: LifeCareerStage[] = [
    {
      stage: "Novice of the Foundation",
      years: "Years 1–6",
      text: `Absorbed fundamental trade skills in ${base.occupation.toLowerCase()}, learning to prioritize durability, attentive care, and humble observation over flashy speed.`,
    },
    {
      stage: "Independent Practitioner",
      years: "Years 7–14",
      text: `Established an autonomous community practice, taking on demanding diagnostic challenges and refining bespoke methods under the guidance of ${rel0}.`,
    },
    {
      stage: "District Conservator & Mentor",
      years: "Years 15–Present",
      text: `Now recognized as a senior specialist in ${ladder.name.toLowerCase()}, coordinating vital restorative work and mentoring a dedicated cohort of junior apprentices.`,
    },
    {
      stage: "Senior Guild Trustee",
      years: "Present & Forward",
      text: `Serves on the municipal consultative bench, arbitrating complex community decisions and safeguarding the unwritten customs of the commonwealth.`,
    },
  ];

  const responsibilities: string[] = [
    `Directing daily operations and diagnostic stewardship in ${base.occupation.toLowerCase()}.`,
    `Mentoring junior associates and apprentices in the ethical codes of ${ladder.name.toLowerCase()}.`,
    `Mediating jurisdictional frictions alongside civic delegates such as ${rel1}.`,
    "Maintaining open-source schematics and records for public community reference.",
    "Ensuring all completed initiatives satisfy the municipal mandate of durability and humane grace.",
    "Preserving the sanctuary atmosphere of the workshop for visiting citizens seeking counsel.",
  ];

  const expertise: string[] = [
    `Bespoke methods in ${base.occupation.toLowerCase()}`,
    `Systemic diagnosis under ${ladder.name.toLowerCase()} protocols`,
    "Non-coercive consensus building and dispute de-escalation",
    "Generational material endurance and preventative care",
    "Empirical field problem-solving with minimal industrial waste",
  ];

  // 5. Standing
  const standing = {
    wealthBracket: ladder.wealthBracket,
    wealthText: ladder.wealthExplanation,
    socialClass: ladder.socialClassLabel,
    reputation: `Revered across the district as a practitioner of unbending honesty, calm humor, and deep reliability.`,
    prestige: ladder.standingLabel,
    influence: `Direct authority within municipal consultative circles, guild reconciliations, and apprentice certifications.`,
    influenceLevel: influenceScore,
    economicCircumstances: `Deeply insulated against financial volatility; supported by universal guest rights, guild shares, and reciprocal community affection.`,
  };

  // 6. Home (dwelling, environment, lifestyle, 4-6 possessions, transport, 6 routine beats "HH:MM — text")
  const dwelling = isHostile
    ? `A stabilized resonance redoubt anchored into a basalt ledge, shielded by harmonic field baffles and glowing with soft bioluminescent warmth.`
    : `A welcoming two-story timber-and-brick lodge with wide open eaves, an adjoining courtyard garden, and fragrant cedar workrooms.`;

  const environment = isHostile
    ? `A stark, awe-inspiring energetic landscape where incandescent plasma currents illuminate crystal canyons under alien constellations.`
    : `A tranquil neighborhood of winding stone lanes, community fruit orchards, and sunlit courtyards overlooking peaceful canals.`;

  const lifestyle = `Early rising, contemplative tea, generous hospitality, patient workshop labor, and long evening conversations with neighbors.`;

  const possessions: string[] = [
    "An antique set of hand-forged brass calipers and diagnostic measuring gauges",
    "Annotated personal journals detailing thirty years of subtle craft observations",
    `A ceramic tea service crafted and gifted by ${rel2} to mark a pivotal shared triumph`,
    "A hand-carved rocking chair positioned by the front window for unhurried thought",
    "An open-face mechanical pocket watch that measures breath cycles rather than rigid minutes",
  ];

  const transport = `Foot travel along stone promenades and a sturdy mechanical transport cycle fitted with leather panniers for tools.`;

  const routine: string[] = [
    "06:30 — Dawn kindle of the hearth, brewing mountain herbal tea, and thirty minutes of silent observation.",
    "07:45 — Opening the workshop shutters, feeding the courtyard birds, and greeting early market travelers.",
    "09:15 — Hands-on diagnostic labor, reviewing ongoing repairs and delegating priorities to apprentices.",
    `12:30 — Communal lunch of fresh sourdough, olive stew, and fruit, shared around the workbench with ${rel2}.`,
    `15:00 — Consultative meeting at the civic hall with ${rel1} to review neighborhood infrastructure proposals.`,
    "18:45 — Securing tools, extinguishing the forge, and enjoying an unhurried evening walk through the canal orchards.",
  ];

  // 7. People (family, relationships, friendships, socialCircle)
  const people = {
    family: `Descended from generations of patient artisans and caretakers who valued a quiet conscience over public spectacle.`,
    relationships: `Maintains a deep, affectionate bond with lifelong companion and collaborator ${rel2}, whose grounded humor balances ${base.name}'s contemplative depth.`,
    friendships: `A trusted circle comprising veteran mentor ${rel0}, civic registrar ${rel1}, and an eclectic fellowship of neighborhood bakers, gardeners, and retired mechanics.`,
    socialCircle: `The Guild of Unhurried Craftsmen, the District Reconciliation Assembly, and the regulars who gather at the neighborhood evening tea table.`,
  };

  // 8. Formative (childhood, education, 3-4 decisions, 3 achievements, 2-3 setbacks, 2-3 turningPoints)
  const decisions: string[] = [
    base.choices[0]?.choice || "Choosing local community stewardship over a lucrative bureaucratic career in the capital.",
    base.choices[1]?.choice || "Refusing to patent a vital diagnostic breakthrough, leaving the schematics free for all workshops.",
    base.choices[2]?.choice || "Taking in three unclassifiable young apprentices who had been rejected by conventional schools.",
    "Investing personal savings into an open community seed vault and public tool repository.",
  ];

  const achievements: string[] = [
    base.dossier.achievements[0] ||
      `Restored the district's primary municipal apparatus during a critical winter freeze without halting water supplies.`,
    base.dossier.achievements[1] ||
      `Authored an open guide on ${ladder.name.toLowerCase()} that resolved a multi-year territorial dispute between neighboring craft lodges.`,
    base.dossier.achievements[2] ||
      `Established a free evening fellowship where young artisans receive mentorship, warm meals, and workspace.`,
  ];

  const setbacks: string[] = [
    base.dossier.failures[0] ||
      "Suffered painful financial losses early in career when a partner absconded with initial guild development credits.",
    base.dossier.failures[1] ||
      "Failed to convince municipal authorities to preserve an ancient willow grove during a rushed roadway expansion.",
  ];

  const turningPoints: string[] = [
    base.story.turningPoint ||
      "The courageous refusal to sign off on a hurried, shoddy construction contract, risking career standing to protect public safety.",
    `The unexpected death of an early mentor, which crystallized ${base.name}'s resolve to dedicate life to living memory and gentle craft.`,
  ];

  const formative = {
    childhood: isHostile
      ? `Formed amid the intense energetic fluxes of ${ctx.universe.name}, developing an early intuitive sensitivity to thermal stability and harmonic calm.`
      : `Raised in an affectionate household where evenings were spent mending clothes, telling stories by firelight, and learning to listen before speaking.`,
    education: `Trained through an accredited guild apprenticeship under ${rel0}, complemented by extensive independent study of historical architecture and restorative philosophy.`,
    decisions,
    achievements,
    setbacks,
    turningPoints,
  };

  // 9. Society (summary, laws, culture, institutions, expectations, opportunity)
  const society = {
    summary: ladder.societySummary,
    laws: ladder.lawsSummary,
    culture: ladder.cultureSummary,
    institutions: ladder.institutionsSummary,
    expectations: ladder.expectationsSummary,
    opportunity: ladder.opportunitySummary,
  };

  // 10. World (technology, science, geography, history, environment)
  const world = {
    technology: isHostile
      ? "Resonant crystalline containment fields, harmonic thermal taps, and non-biological energetic memory matrices."
      : "Durable mechanical linkages, clean hydronic power networks, modular joinery, and non-polluting solar ceramics.",
    science: isHostile
      ? "Advanced field dynamics, radiant plasma thermodynamics, and cohesive consciousness matrices."
      : "Empirical ecology, materials longevity science, acoustic architecture, and restorative human psychology.",
    geography: isHostile
      ? "Towering crystalline basalt arches suspended over glowing radiant thermal currents and magnetic mist canyons."
      : "Rolling temperate river basins bordered by ancient forested hills, stone viaducts, and fertile agricultural terraces.",
    history: isHostile
      ? "A resilient civilization that evolved to thrive in high-energy physics regimes through cooperative coherence."
      : "Three centuries of uninterrupted municipal peace following the peaceful dissolution of centralized commercial empires.",
    environment: isHostile
      ? "Intensely radiant yet pristine, devoid of biological decay and animated by celestial plasma auroras."
      : "Pristine air, crystal-clear riverways, and thriving urban birdlife fostered by organic building standards.",
  };

  // 11. How Differs (headline, 3 paragraphs, 4-5 keyDifferences)
  const howDiffers = {
    headline: `A society that inverted Earth's worship of aggressive velocity, finding true greatness in patient, restorative human presence.`,
    paragraphs: [
      `Where modern Earth culture equates success with frantic productivity, conspicuous accumulation, and high-decibel personal branding, this reality operates under an inverted premise. Here, status is awarded to those who finish what others abandon, who listen before passing judgment, and who maintain the fragile fabric of community without demanding applause.`,
      `Economic institutions reflect this humane philosophy. Capital cannot be accumulated endlessly into private dynastic fortresses; instead, wealth flows naturally into shared workshops, public orchards, and educational trusts. Citizens are freed from the chronic anxiety of eviction and precarity, allowing work to be chosen out of authentic calling rather than panic.`,
      `The psychological effect is transformative. Without the constant terror of falling behind or the exhaustion of curating an idealized corporate image, human beings treat one another with disarming gentleness. What an Earth observer might misinterpret as a lack of ambition is, in truth, the rarest achievement of all: a civilization that has learned when it has enough.`,
    ],
    keyDifferences: [
      {
        title: "Inversion of Status",
        text: ladder.invertedRule,
      },
      {
        title: "Abolition of Precarity",
        text: "Universal access to basic food, shelter, and workspace ensures no citizen can be coerced into soul-destroying labor.",
      },
      {
        title: "Generational Durability",
        text: "Architecture, tools, and civic policies are designed to endure for a century rather than discarded for quarterly revenue targets.",
      },
      {
        title: "Celebration of Vulnerability",
        text: "Mistakes and blunders are examined with compassionate curiosity as shared learning opportunities rather than hidden in shame.",
      },
    ],
  };

  // 12. Day In Life (title: exactly "A Day in This Life", 8 beats 2-4 sentences each, closing 1-2 sentences)
  const beats: LifeBeat[] = [
    {
      time: "06:15",
      heading: "Quiet Kindle",
      text: `Awakening in the quiet second-story bedroom, ${base.name} checks the weather vane and listens to the morning breeze moving through the cedar eaves. After rekindling the small hearth oven, they brew a steaming bowl of roasted chicory and sit by the window in unhurried silence. The day begins not with the jolt of an alarm, but with the steady return of conscious breath.`,
    },
    {
      time: "07:30",
      heading: "Opening the Threshold",
      text: `Stepping down the wooden stairs, ${base.name} unlocks the heavy oak doors of the workshop and sweeps the stone threshold. A passing neighbor nods from across the lane, and an early courier delivers a pouch of hand-rolled beeswax candles. The morning air smells of damp slate and river mist, clear and inviting.`,
    },
    {
      time: "09:00",
      heading: "The Diagnostic Bench",
      text: `At the main bench, ${base.name} examines a delicate measuring instrument brought in by an anxious young apprentice from the lower harbor. With calm hands and patient humor, ${base.name} demonstrates how adjusting a single brass pivot relieves structural strain across the entire mechanism. The apprentice exhales in relief, realizing the error was not a failure but an essential lesson in leverage.`,
    },
    {
      time: "11:15",
      heading: "Mentor's Hearth",
      text: `Senior advisor ${rel0} stops by the workshop carrying a basket of warm spiced buns and an inquiry regarding the upcoming seasonal guild elections. Sitting together beside the cooling hearth, the two discuss the delicate balance of district water rights, weighing long-term soil health against immediate harvest needs. Their conversation flows with the effortless rhythm of thirty years of mutual trust.`,
    },
    {
      time: "13:00",
      heading: "Midday Table",
      text: `Lifelong companion ${rel2} arrives with a kettle of lentil stew and a loaf of seeded bread, spreading out lunch on the corner of the drafting table. They trade playful banter about an eccentric municipal ordinance before turning to a quiet domestic plan for planting autumn apple saplings. For a full hour, tools are set aside and the workshop is filled with warmth and easy laughter.`,
    },
    {
      time: "15:00",
      heading: "Civic Mediation",
      text: `A brief friction arises at the district hall, where municipal registrar ${rel1} has summoned ${base.name} to arbitrate a property boundary dispute between two proud master weavers. Instead of quoting dry statutes, ${base.name} invites both parties to sit in three minutes of shared silence before asking each to articulate the other's grievance. By the time tea is poured, the dispute has dissolved into mutual concession.`,
    },
    {
      time: "17:45",
      heading: "The Apprentice Circle",
      text: `Late afternoon is dedicated to the workshop apprentices, who gather around the central lathe for an open-ended practical demonstration in ${base.occupation.toLowerCase()}. ${base.name} emphasizes the importance of material feel and acoustic tuning, encouraging each youth to trust their senses rather than rush toward completion. As twilight gathers, the workbenches are cleaned and oiled with practiced care.`,
    },
    {
      time: "20:00",
      heading: "Lamp-Lit Hearth",
      text: `After securing the outer bolts and extinguishing the workshop lanterns, ${base.name} climbs upstairs to enjoy a simple dinner of roasted squash and cheese. Sitting in the worn armchair with a notebook of sketches, they record a few quiet reflections on the day's encounters while dusk settles over the rooftops. Outside, the bells of the central clocktower chime nine times, gentle and unhurried.`,
    },
  ];

  const dayInLife = {
    title: "A Day in This Life",
    beats,
    closing: `As lantern reflections shimmer across the dark canal waters, peace settles over the dwelling, accompanied by the quiet knowledge that another day was lived with honor, gentleness, and authentic purpose.`,
  };

  // 13. Futures (title "Three Possible Futures", exactly 3 paths a, b, c with flavors, 3 opportunities, 3 challenges each, disclaimer)
  const futures: LifeFuturePath[] = [
    {
      id: "a",
      name: "High Custodian of the Province",
      flavor: "Most natural",
      summary: `Gradual formal elevation to the High Consultative Bench of the province, codifying restorative ethics across the regional commonwealth.`,
      opportunities: [
        "Institutionalizing preventative repair across thirty-four municipal districts",
        "Expanding free apprenticeships for vulnerable and displaced provincial youth",
        "Publishing the definitive open-source atlas of humane community craftsmanship",
      ],
      challenges: [
        "Managing the heavy diplomatic burden of reconciling traditionalist trade syndicates",
        "Protecting personal quiet time from endless council committee obligations",
        "Ensuring regional authority does not dilute the intimate warmth of neighborhood ties",
      ],
      consequences: `Universal veneration across the commonwealth as an architect of lasting peace, at the cost of day-to-day hands-on benchwork.`,
    },
    {
      id: "b",
      name: "Founder of the Wandering Ateliers",
      flavor: "Ambitious",
      summary: `Commissioning a fleet of traveling workshop barges to bring restorative tools, medicine, and free craft education to remote perimeter hamlets.`,
      opportunities: [
        "Revitalizing isolated frontier valleys that lack access to master artisans",
        "Cross-pollinating forgotten folk methodologies into mainstream guild practices",
        "Establishing permanent mutual-aid covenants across formerly estranged provinces",
      ],
      challenges: [
        "Enduring the physical rigors and weather exposure of itinerant river travel",
        "Navigating jurisdictional friction with suspicious outer territorial marshals",
        "Securing ongoing material supplies without accepting commercial compromise",
      ],
      consequences: `A legendary regional folklore etched into river towns for generations, transforming the lives of thousands of frontier families.`,
    },
    {
      id: "c",
      name: "Master of the Silent Hermitage",
      flavor: "Wildcard",
      summary: `Retiring early from all public offices to establish a contemplative mountain sanctuary dedicated to deep listening, rare botanicals, and handcraft.`,
      opportunities: [
        "Experiencing profound, uninterrupted communion with wild natural cycles",
        "Cultivating heirloom medicinal plants and developing gentle restorative tinctures",
        "Serving as an unpressured sanctuary refuge for burned-out civic leaders and travelers",
      ],
      challenges: [
        "Adapting to the austere simplicity and winter isolation of high alpine living",
        "Resisting persistent pleas from city delegates urging a return to municipal politics",
        "Managing physical labor without a team of younger apprentices on hand",
      ],
      consequences: `Deep spiritual serenity and an enduring legacy as an elusive, beloved mountain sage whose counsel is sought only in moments of deepest crisis.`,
    },
  ];

  const futuresSection = {
    title: "Three Possible Futures",
    paths: futures,
    disclaimer: `These forward branches represent imaginative fictional trajectories generated by the multiverse model, not fixed destinies or real-world predictions.`,
  };

  // 14. Timeline (9-10 entries, ascending age, covering origin, education, choice, achievement, setback, turning, present, future)
  const tAge1 = 6;
  const tAge2 = 14;
  const tAge3 = 19;
  const tAge4 = 25;
  const tAge5 = 31;
  const tAge6 = Math.max(34, age - 5);
  const tAge7 = Math.max(tAge6 + 2, age - 2);
  const tAge8 = age;
  const tAge9 = age + 8;
  const tAge10 = age + 16;

  const timeline: LifeTimelineEntry[] = [
    {
      age: tAge1,
      label: `Age ${tAge1}`,
      title: "The Childhood Hearth",
      text: `Learns early lessons in patience and observation while sitting beside family elders in the workshop courtyard.`,
      kind: "origin",
    },
    {
      age: tAge2,
      label: `Age ${tAge2}`,
      title: "Apprenticeship Entry",
      text: `Formally inducted into the local trade lodge under the patient tutelage of master craftsman ${rel0}.`,
      kind: "education",
    },
    {
      age: tAge3,
      label: `Age ${tAge3}`,
      title: "The Forked Road",
      text: base.choices[0]?.choice || "Elects an unhurried municipal craft path over lucrative private mercantile offers.",
      kind: "choice",
    },
    {
      age: tAge4,
      label: `Age ${tAge4}`,
      title: "First Civic Breakthrough",
      text: `Successfully re-engineers the district water filtration gates, earning the enduring respect of neighborhood families.`,
      kind: "achievement",
    },
    {
      age: tAge5,
      label: `Age ${tAge5}`,
      title: "The Severe Winter Crucible",
      text: `Survives catastrophic seasonal floods and material shortages, maintaining workshop heat and soup for fifty neighbors.`,
      kind: "setback",
    },
    {
      age: tAge6,
      label: `Age ${tAge6}`,
      title: "The Defining Stance",
      text: (base.story?.turningPoint ?? "").length > 140 ? (base.story?.turningPoint ?? "").slice(0, 137) + "..." : (base.story?.turningPoint ?? ""),
      kind: "turning",
    },
    {
      age: tAge7,
      label: `Age ${tAge7}`,
      title: "Founding the Workshop",
      text: `Establishes the independent atelier in the canal quarter alongside lifelong collaborator ${rel2}.`,
      kind: "achievement",
    },
    {
      age: tAge8,
      label: `Age ${tAge8}`,
      title: "Present Mastery",
      text: `Operates as ${base.occupation} and recognized elder in ${ladder.name.toLowerCase()}, guiding apprentices and arbitrating civic peace.`,
      kind: "present",
    },
    {
      age: tAge9,
      label: `Age ${tAge9}`,
      title: "Regional Elevation",
      text: `Anticipates appointment to the High Consultative Bench to codify inter-provincial craft covenants.`,
      kind: "future",
    },
    {
      age: tAge10,
      label: `Age ${tAge10}`,
      title: "Generational Legacy",
      text: `Envisions handing over the workshop keys to senior apprentices while establishing a quiet mountain sanctuary.`,
      kind: "future",
    },
  ];

  // 15. Earth Comparison (basis: "general-reference", 8 rows in exact EarthDimension order, 3-4 divergences d1..d4, visitorSignals)
  const divergences: EarthDivergence[] = [
    ladder.divergenceD1,
    ladder.divergenceD2,
    ladder.divergenceD3,
    ladder.divergenceD4,
  ];

  const rows: EarthRow[] = [
    {
      dimension: "Occupation",
      here: base.occupation,
      earth: "Corporate analyst, project manager, or gig-economy specialist",
      because: `Because ${lower(ladder.divergenceD1.title)} elevated maintenance, care, and diagnostic craftsmanship above frantic commercial sales cycles.`,
      divergenceId: "d1",
    },
    {
      dimension: "Social status",
      here: ladder.standingLabel,
      earth: "Middle-income employee evaluated by metrics and job titles",
      because: `Because ${lower(ladder.divergenceD3.title)} reversed Earth's status hierarchy, honoring quiet community preservers over self-promoting influencers.`,
      divergenceId: "d3",
    },
    {
      dimension: "Financial circumstances",
      here: ladder.wealthBracket,
      earth: "Debt-sensitive wage earner vulnerable to rent, inflation, and layoffs",
      because: `Because ${lower(ladder.divergenceD2.title)} guaranteed universal access to life essentials and outlawed speculative financial stripping.`,
      divergenceId: "d2",
    },
    {
      dimension: "Relationships",
      here: "Lifelong multi-generational ties forged in shared workshops and mutual aid",
      earth: "Fragmented, geographically mobile networks maintained via digital feeds",
      because: `Because ${lower(ladder.divergenceD4.title)} created durable physical commons where daily face-to-face interdependence is woven into neighborhood survival.`,
      divergenceId: "d4",
    },
    {
      dimension: "Freedoms",
      here: "Total personal sovereignty over schedule, craft ethics, and creative speed",
      earth: "Extensive consumer choices paired with severe workplace surveillance and time precarity",
      because: `Because ${lower(ladder.divergenceD1.title)} rejected artificial corporate deadlines, making individual human dignity an inviolable legal right.`,
      divergenceId: "d1",
    },
    {
      dimension: "Daily life",
      here: "Tactile, rhythmic, deeply unhurried labor balanced by communal meals and rest",
      earth: "Persistent screen immersion, notification anxiety, and commuter stress",
      because: `Because ${lower(ladder.divergenceD2.title)} restructured urban geography around walkable courtyards and organic human tempos rather than industrial throughput.`,
      divergenceId: "d2",
    },
    {
      dimension: "Available technology",
      here: "Centurial mechanical systems, open-source schematics, and repairable tools",
      earth: "Proprietary consumer electronics with planned obsolescence and locked firmware",
      because: `Because ${lower(ladder.divergenceD4.title)} mandated that every manufactured apparatus carry accessible repair schematics and standard parts.`,
      divergenceId: "d4",
    },
    {
      dimension: "Future opportunities",
      here: "Lifelong civic veneration, mentoring younger cohorts, and peaceful retirement",
      earth: "Volatile career pivots driven by technological obsolescence and automation dread",
      because: `Because ${lower(ladder.divergenceD3.title)} built stable generational ladders rooted in human wisdom that no machine or market panic can devalue.`,
      divergenceId: "d3",
    },
  ];

  // Visitor signals (3-5 strings using ctx.resolved, noting skipped answers if generator filled them)
  const visitorSignals: string[] = [];

  if (ctx.resolved.interests.length > 0) {
    const intList = listJoin(ctx.resolved.interests.map((i) => i));
    if (ctx.resolved.filled.interests) {
      visitorSignals.push(
        `Because you skipped interests, the generator selected an affinity for ${intList}, which shaped this self's craft as ${base.occupation.toLowerCase()}.`
      );
    } else {
      visitorSignals.push(
        `Your stated interest in ${intList} was radically reframed through the inverted social values of "${ladder.name}."`
      );
    }
  }

  if (ctx.resolved.strengths.length > 0) {
    const strList = listJoin(ctx.resolved.strengths.map((s) => s));
    if (ctx.resolved.filled.strengths) {
      visitorSignals.push(
        `The generator assigned ${strList} as your guiding virtue, anchoring this persona's quiet moral spine under civic pressure.`
      );
    } else {
      visitorSignals.push(
        `Your declared strength of ${strList} provided the psychological endurance needed to thrive outside conventional Earth ladders.`
      );
    }
  }

  if (ctx.resolved.ambition) {
    if (ctx.resolved.filled.ambition) {
      visitorSignals.push(
        `Your ambition was filled by the generator toward ${ctx.resolved.ambition}, which channeled this persona's focus into generational community stewardship.`
      );
    } else {
      visitorSignals.push(
        `Your ambition to ${ctx.resolved.ambition} directly influenced this life's choice to build enduring local trust rather than fleeting personal fame.`
      );
    }
  }

  if (ctx.resolved.lifePath) {
    if (ctx.resolved.filled.lifePath) {
      visitorSignals.push(
        `Because life path was left open, the generator selected ${ctx.resolved.lifePath}, placing this self at the center of neighborhood continuity.`
      );
    } else {
      visitorSignals.push(
        `Your chosen disposition as "${ctx.resolved.lifePath}" defined how ${base.name} responds to institutional friction with generous, unhurried resolve.`
      );
    }
  }

  const earthComparison = {
    basis: "general-reference" as const,
    basisNote:
      "The Earth column serves as a general reference for typical modern professional life, not personal facts about you, because the questionnaire gathers no private personal data.",
    rows,
    divergences,
    visitorSignals,
  };

  return {
    engineVersion: ENGINE_VERSION,
    identity: {
      fullName: base.name,
      age,
      ageNote,
      personalitySummary: base.dossier.personality,
      biography,
    },
    work: {
      occupation: base.occupation,
      expertise,
      careerHistory,
      responsibilities,
      workplace: ladder.workplaceType,
    },
    standing,
    home: {
      dwelling,
      environment,
      lifestyle,
      possessions,
      transport,
      routine,
    },
    people,
    formative,
    society,
    world,
    howDiffers,
    dayInLife,
    futures: futuresSection,
    timeline,
    earthComparison,
  };
}

/**
 * Backward compatibility alias: the original stub mistakenly exported buildStrangeLife.
 */
export const buildStrangeLife = buildUnexpectedLife;
